import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useCatalog } from '../../../hooks/useCatalog';
import { useUpload } from '../../../hooks/useUpload';
import { useCatalogDescriptions } from '../../../hooks/useCatalogDescriptions';
import { useCatalogMeasurements } from '../../../hooks/useCatalogMeasurements';
import { useUserProfile } from '../../../hooks/useUserProfile';
import { useActiveCatalog } from '../../../hooks/useActiveCatalog';
import { useAuthorization } from '../../../hooks/useAuthorization';
import { useAutosaveStatus } from '../../../hooks/useAutosaveStatus';
import type {
  AdditionalInfoData,
  DescriptionData as SaveDescriptionData,
  MeasurementData as SaveMeasurementData,
} from '../../../contexts/CatalogContext';
import type { Measurement } from '../../../components/MeasurementField';
import type { ObjectMetadataValue } from '../../../types/ObjectHierarchicalMetadataValue';
import { getDefaultUnitForDimension } from '../../../constants/measurementDimensions';
import { logger } from '../../../utils/logger';

/**
 * Height and Width are always shown in the Measurements section, even when
 * the catalog item has no dimensions saved yet — a 2D artwork without H/W
 * is the rare case, not the norm. `ensureRequiredDimensions` prepends empty
 * placeholders for whichever of the two are missing.
 */
export const REQUIRED_DIMENSION_TYPES = ['Height', 'Width'] as const;
export const ensureRequiredDimensions = (
  dimensions: Measurement[],
  fallbackUnit: string,
): Measurement[] => {
  const missing = REQUIRED_DIMENSION_TYPES.filter(
    (type) => !dimensions.some((d) => d.type === type),
  ).map((type) => ({
    type,
    value: '',
    fraction: '',
    unit: getDefaultUnitForDimension(type) || fallbackUnit,
  }));
  return [...missing, ...dimensions];
};

/**
 * Single source of form state for the Artwork Details page.
 *
 * Owns *all* fields that span the 8 design sections (creators, title,
 * media description, signature, creation date, measurements, multiples /
 * editions, additional notes). Loads description + measurements records
 * from the backend on mount and debounces saves at 2s.
 *
 * The wizard's `DescribeTab` / `MediaTab` / `MeasureTab` continue to use
 * their own copies of these hooks for `/upload-object`. The two paths
 * race on the backend record only if the user has both pages open at
 * once — no different from the existing situation.
 */

const SAVE_DEBOUNCE_MS = 2000;

export interface Creator {
  name: string;
  isCurrentUser: boolean;
  notes?: string;
}

export interface Signature {
  type: string[];
  location: string[];
  display: string;
}

export interface CreationDate {
  type: string;
  value: string;
  location: string[];
  display: string;
}

// Default to "Year" because year-only is overwhelmingly the most common
// creation-date precision in art cataloging — falling back to a blank type
// forced users to pick one before entering anything.
export const INITIAL_CREATION_DATE: CreationDate = {
  type: 'Year',
  value: '',
  location: [],
  display: '',
};

/**
 * Normalize a creationDate record loaded from the backend. Older records
 * persisted while the default was blank may have `type: ''` or no type at
 * all — coerce those to 'Year' so the dropdown never renders empty when
 * a user re-opens an old item.
 */
export const normalizeLoadedCreationDate = (cd: unknown): CreationDate => {
  if (!cd || typeof cd !== 'object') return INITIAL_CREATION_DATE;
  const raw = cd as Partial<CreationDate> & { location?: unknown };
  const location = Array.isArray(raw.location)
    ? (raw.location as string[])
    : raw.location
      ? [String(raw.location)]
      : [];
  return {
    type: raw.type || 'Year',
    value: raw.value ?? '',
    location,
    display: raw.display ?? '',
  };
};

export type { Measurement };

export interface Edition {
  type: string;
  count: number;
  total: number;
}

export interface ArtworkDetailsForm {
  // Loading
  isLoading: boolean;
  hasError: boolean;

  // Creators
  creators: Creator[];
  setCreators: (next: Creator[]) => void;
  isCommercial: boolean;
  ownerDisplayName: string;

  // Title
  title: string;
  setTitle: (next: string) => void;
  titleLocation: string[];
  setTitleLocation: (next: string[]) => void;
  titleAsWritten: string;
  setTitleAsWritten: (next: string) => void;
  alternativeTitles: string[];
  setAlternativeTitles: (next: string[]) => void;

  // Media
  mediaDescription: string;
  setMediaDescription: (next: string) => void;

  // Signature
  signatures: Signature[];
  setSignatures: (next: Signature[]) => void;

  // Creation Date
  creationDate: CreationDate;
  setCreationDate: (next: CreationDate) => void;

  // Measurements
  unit: string;
  setUnit: (next: string) => void;
  dimensions: Measurement[];
  setDimensions: (next: Measurement[]) => void;

  // Substrate Measurements (separate physical support: e.g. paper inside a frame).
  // When false, substrate dimensions are not persisted even if entered.
  hasDifferentSubstrate: boolean;
  setHasDifferentSubstrate: (next: boolean) => void;
  substrateUnit: string;
  setSubstrateUnit: (next: string) => void;
  substrateDimensions: Measurement[];
  setSubstrateDimensions: (next: Measurement[]) => void;

  // Multiples / Editions
  hasMultiples: boolean;
  setHasMultiples: (next: boolean) => void;
  editions: Edition[];
  setEditions: (next: Edition[]) => void;

  // Additional Notes (single textarea bound to descriptions.stepNotes.notes)
  notes: string;
  setNotes: (next: string) => void;

  // Manual save trigger (used by inputs on blur)
  flushSave: () => void;
}

/**
 * Build the additional-info payload for the autosave PUT.
 *
 * The Hi-Fi Edit Object redesign has no form inputs for valuations, condition
 * notes, or marked-as-sold — those still live in the legacy wizard's Private
 * Only section. The endpoint at `POST /api/additional-info/{id}` does a full
 * row replace (Pydantic `exclude_none=True` + `repo.put`), so every field not
 * in the payload is wiped or reset to its default. The carry-through must
 * forward every field on the loaded record so an autosave triggered by edits
 * elsewhere on the page can never silently drop data the form doesn't render.
 *
 * @param carry The loaded additional-info record (null for fresh items).
 * @param notesText The current value of the public-notes textarea.
 */
export const buildAdditionalInfoPayload = (
  carry: AdditionalInfoData | null,
  notesText: string,
): AdditionalInfoData => {
  const trimmed = notesText.trim();
  return {
    relatedWorks: carry?.relatedWorks ?? [],
    privateNotes: carry?.privateNotes ?? [],
    physicalLocation: carry?.physicalLocation ?? '',
    estimatedValue: carry?.estimatedValue ?? '',
    ...(carry?.valuations ? { valuations: carry.valuations } : {}),
    ...(carry?.markedAsSold !== undefined ? { markedAsSold: carry.markedAsSold } : {}),
    ...(carry?.conditionNotes !== undefined ? { conditionNotes: carry.conditionNotes } : {}),
    ...(carry?.exhibitions ? { exhibitions: carry.exhibitions } : {}),
    stepNotes: carry?.stepNotes ?? {},
    publicNotes: trimmed ? [trimmed] : [],
  };
};

export const useArtworkDetailsForm = (
  catalogItemId?: string,
  ownerId?: string | null,
  /**
   * Explicit create-vs-edit signal forwarded from EditObject/DescribeStep
   * (GH#1225). When false (create wizard), a filename-derived title
   * suggestion set by UploadTab on the first image upload is applied to an
   * empty Title field. When true (edit mode) the suggestion is never
   * applied. Mirrors the legacy `wizard/DescribeTab`'s `isEditMode` gate —
   * this hook is the redesigned `/edit-object` flow's independent copy of
   * that same load/title logic (see module doc above).
   */
  isEditMode = false,
): ArtworkDetailsForm => {
  const { currentItem, getAdditionalInfo, saveAdditionalInfo } = useCatalog();
  const { catalogId, suggestedTitle, catalogTitle } = useUpload();
  const effectiveCatalogId = catalogItemId || catalogId || currentItem?.catalogItemId;

  const { loadDescription, updateDescription } = useCatalogDescriptions(
    effectiveCatalogId,
    ownerId,
  );
  const { loadMeasurements, updateMeasurements } = useCatalogMeasurements(
    effectiveCatalogId,
    ownerId,
  );

  const { displayName } = useUserProfile();
  const { activeDelegation } = useActiveCatalog();
  const ownerDisplayName = activeDelegation?.delegatorName ?? displayName ?? '';
  const { isCommercialTier } = useAuthorization();
  const isCommercial = isCommercialTier();

  const { setStatus, setLastSaveTime } = useAutosaveStatus();

  // -------- State --------
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const [creators, setCreators] = useState<Creator[]>([]);
  // GH#1225 (3rd bounce): seed from the UploadContext `catalogTitle` stash
  // on mount. In create mode, before a catalogId exists, a typed title
  // lives only in this hook's local state — navigating to another step
  // unmounts this component and would otherwise throw it away. `DescribeStep`
  // mirrors every live edit up to `UploadContext.catalogTitle`, which
  // survives across step remounts (the provider wraps the whole EditObject
  // flow), so re-hydrating from it here recovers a typed-but-unsaved title
  // and — critically — makes the apply-time guard below (`titleRef.current`)
  // see it as non-empty, blocking the filename suggestion from clobbering it.
  const [title, setTitle] = useState(() => (!isEditMode ? catalogTitle : ''));
  const [titleLocation, setTitleLocation] = useState<string[]>([]);
  const [titleAsWritten, setTitleAsWritten] = useState('');
  const [alternativeTitles, setAlternativeTitles] = useState<string[]>([]);
  const [mediaDescription, setMediaDescription] = useState('');
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [creationDate, setCreationDate] = useState<CreationDate>(INITIAL_CREATION_DATE);
  const [unit, setUnit] = useState('inches');
  const [dimensions, setDimensions] = useState<Measurement[]>([]);
  const [hasDifferentSubstrate, setHasDifferentSubstrate] = useState(false);
  const [substrateUnit, setSubstrateUnit] = useState('inches');
  const [substrateDimensions, setSubstrateDimensions] = useState<Measurement[]>([]);
  const [hasMultiples, setHasMultiples] = useState(false);
  const [editions, setEditions] = useState<Edition[]>([]);
  const [notes, setNotes] = useState('');

  // Carry-through ref for fields owned by the description record but not
  // surfaced in the design's 8 sections (workType, techniques, materials,
  // substrate, mediaAdditionalComments). They live on the Getty Vocab step;
  // we preserve them here so saves don't wipe them.
  const carryRef = useRef<{
    workType: ObjectMetadataValue | null;
    techniques: ObjectMetadataValue[];
    materials: ObjectMetadataValue[];
    substrate: ObjectMetadataValue[];
    mediaAdditionalComments?: string;
  }>({
    workType: null,
    techniques: [],
    materials: [],
    substrate: [],
  });

  // Carry-through for AdditionalInfo fields owned by Private Only step
  // (privateNotes, physicalLocation, estimatedValue, relatedWorks,
  // exhibitions). Artwork Details only edits `publicNotes` from this
  // record but must preserve the rest on save.
  const addtlCarryRef = useRef<AdditionalInfoData | null>(null);

  const loadedCatalogIdRef = useRef<string | null>(null);
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSerializedRef = useRef<string>('');

  // GH#1225: ref that always reflects the latest rendered title value, so
  // the load's async `.then()` callback (below) can check the current
  // in-memory title without a stale-closure risk — mirrors the identical
  // pattern in `wizard/DescribeTab.tsx`.
  const titleRef = useRef(title);
  titleRef.current = title;

  // GH#1225: apply the filename-derived suggestion (set by UploadTab on the
  // first image upload to a brand-new object) when the loaded title is
  // empty. Guards:
  //   1. !isEditMode          — never auto-title in edit mode
  //   2. !loadedTitle         — DB row has no title yet (or no row at all)
  //   3. !titleRef.current    — in-memory title is also empty (protects a
  //                             user-typed-but-not-yet-saved title)
  //   4. suggestedTitle       — a filename-derived suggestion is available
  const maybeApplySuggestedTitle = (loadedTitle: string | undefined | null) => {
    if (!isEditMode && !loadedTitle?.trim() && !titleRef.current?.trim() && suggestedTitle?.trim()) {
      setTitle(suggestedTitle);
    }
  };

  // -------- Load --------
  useEffect(() => {
    // GL#46: when there is no catalogId yet (no image uploaded, not in edit
    // mode), surface an empty editable form immediately instead of leaving
    // isLoading=true forever. The autosave effect also guards on
    // effectiveCatalogId, so nothing will persist until the ID exists.
    if (!effectiveCatalogId) {
      setIsLoading(false);
      return;
    }
    if (loadedCatalogIdRef.current === effectiveCatalogId) {
      return;
    }
    loadedCatalogIdRef.current = effectiveCatalogId;
    setIsLoading(true);
    setHasError(false);

    const loadAddtl = effectiveCatalogId
      ? getAdditionalInfo(effectiveCatalogId, ownerId).catch((err) => {
          logger.warn('[useArtworkDetailsForm] additional-info load failed', err);
          return null as AdditionalInfoData | null;
        })
      : Promise.resolve(null as AdditionalInfoData | null);

    Promise.all([loadDescription(), loadMeasurements(), loadAddtl])
      .then(([desc, meas, addtl]) => {
        if (desc) {
          // Creators — preserve the catalog owner's name display.
          const loadedCreators = (desc.creators ?? []) as Creator[];
          setCreators(
            isCommercial
              ? loadedCreators
              : loadedCreators.map((c) =>
                  c.isCurrentUser
                    ? { ...c, name: c.name?.trim() || ownerDisplayName }
                    : c,
                ),
          );

          // GH#1225 (3rd bounce, code review finding): `getDescription` never
          // resolves null for a real catalogId — a 404 (no description row
          // yet) resolves to a default object with `title: ''`, so `desc`
          // is always truthy here. Blindly doing `setTitle(desc.title ?? '')`
          // clobbered the remount-seeded stash (see `titleRef` above) back to
          // '' before `maybeApplySuggestedTitle` ever ran, which then also
          // read a stale-empty `titleRef` and let the filename suggestion
          // apply — losing the typed title all over again. In create mode,
          // an empty DB title must not stomp whatever is already in memory
          // (the stash-seeded or user-typed value); in edit mode a saved
          // blank title is authoritative and must still load as blank.
          setTitle(desc.title?.trim() ? desc.title : !isEditMode ? titleRef.current : '');
          setTitleLocation(
            Array.isArray(desc.titleLocation)
              ? desc.titleLocation
              : desc.titleLocation
                ? [desc.titleLocation]
                : [],
          );
          setTitleAsWritten(desc.titleAsWritten ?? '');
          setAlternativeTitles(desc.alternativeTitles ?? []);
          setMediaDescription(desc.mediaDescription ?? '');

          // Signatures — normalize legacy string formats to arrays.
          const rawSigs = desc.signatures;
          let parsedSigs: Signature[] = [];
          if (Array.isArray(rawSigs)) {
            parsedSigs = rawSigs.map((s) => ({
              type: Array.isArray(s.type) ? s.type : s.type ? [String(s.type)] : [],
              location: Array.isArray(s.location)
                ? s.location
                : s.location
                  ? [String(s.location)]
                  : [],
              display: s.display ?? '',
            }));
          } else if (typeof rawSigs === 'string' && rawSigs) {
            try {
              const parsed = JSON.parse(rawSigs);
              if (Array.isArray(parsed)) {
                parsedSigs = parsed;
              }
            } catch (err) {
              logger.warn(
                '[useArtworkDetailsForm] failed to parse legacy signature string',
                err,
              );
            }
          }
          setSignatures(parsedSigs);

          // Creation date — normalize via the shared helper so the
          // backward-compat fallback ('' → 'Year') stays under test.
          if (desc.creationDate && typeof desc.creationDate === 'object') {
            setCreationDate(normalizeLoadedCreationDate(desc.creationDate));
          }

          // Notes load + migration is handled below, after `meas` is read,
          // so legacy MeasureTab notes (`meas.stepNotes.notes`) are merged
          // forward into the description record's `stepNotes.notes`.

          // Multiples / editions
          setHasMultiples(!!desc.hasMultiples);
          setEditions(desc.editions ?? []);

          // Carry-through (Getty fields). mediaAdditionalComments is owned
          // by the Getty Vocab step (MediaTab) — preserved here so saves
          // from this step don't wipe it.
          carryRef.current = {
            workType: desc.workType ?? null,
            techniques: desc.techniques ?? [],
            materials: desc.materials ?? [],
            substrate: desc.substrate ?? [],
            mediaAdditionalComments: desc.mediaAdditionalComments ?? '',
          };
        }

        if (meas) {
          const globalUnit = meas.unit ?? 'inches';
          setUnit(globalUnit);
          const loaded = (meas.dimensions ?? []).map((d) => ({
            type: d.type ?? '',
            value: d.value !== undefined && d.value !== null ? String(d.value) : '',
            fraction: d.fraction ?? '',
            unit: d.unit ?? globalUnit,
          }));
          setDimensions(ensureRequiredDimensions(loaded, globalUnit));
          setHasDifferentSubstrate(!!meas.hasDifferentSubstrate);
          const subUnit = meas.substrateMeasurements?.unit ?? globalUnit;
          setSubstrateUnit(subUnit);
          const subDims = (meas.substrateMeasurements?.dimensions ?? []).map((d) => ({
            type: d.type ?? '',
            value: d.value !== undefined && d.value !== null ? String(d.value) : '',
            fraction: d.fraction ?? '',
            unit: d.unit ?? subUnit,
          }));
          setSubstrateDimensions(ensureRequiredDimensions(subDims, subUnit));
        } else {
          // New artwork or measurements record absent — still surface H/W
          // so the user always sees those required dimensions.
          setDimensions(ensureRequiredDimensions([], 'inches'));
          setSubstrateDimensions(ensureRequiredDimensions([], 'inches'));
        }

        // Carry-through for AdditionalInfo (privateNotes / location / value /
        // exhibitions / relatedWorks) so a save from this page doesn't drop
        // anything edited via PrivateOnlyStep.
        addtlCarryRef.current = addtl ?? null;

        // Notes — merge sources into the single textarea. The Hi-Fi
        // "Additional Notes" section is the public-catalog notes editor
        // (matching the Hi-Fi copy "These notes are part of the public
        // catalog entry"), so the canonical store is
        // `additionalInfo.publicNotes`. Legacy locations
        // (`descriptions.stepNotes.notes` and `measurements.stepNotes.notes`)
        // are merged forward and cleared on next save.
        const collectFromStepNotes = (sn: Record<string, string[]> | undefined) => {
          if (!sn) return [] as string[];
          const ordered = sn.notes ?? Object.values(sn).flat();
          return (ordered ?? []).filter(Boolean);
        };
        const merged = [
          ...(addtl?.publicNotes ?? []).filter(Boolean),
          ...collectFromStepNotes(desc?.stepNotes),
          ...collectFromStepNotes(meas?.stepNotes),
        ];
        setNotes(merged.join('\n\n'));

        // GH#1225: apply the filename-derived title suggestion after the
        // load settles — covers both "no description row yet" (desc is
        // null) and "row exists but title is empty" (desc.title is '').
        maybeApplySuggestedTitle(desc?.title);
      })
      .catch((err) => {
        logger.error('[useArtworkDetailsForm] load failed', err);
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCatalogId]);

  // -------- Save --------
  const buildPayload = useCallback((): {
    desc: SaveDescriptionData;
    meas: SaveMeasurementData;
    addtl: AdditionalInfoData | null;
  } => {
    const desc: SaveDescriptionData = {
      creators: creators.filter((c) => c?.name?.trim()),
      title,
      titleLocation,
      alternativeTitles: alternativeTitles.filter((t) => t.trim()),
      titleAsWritten,
      signatures,
      creationDate,
      mediaDescription,
      hasMultiples,
      editions,
      ...carryRef.current,
      // Cleared on save: legacy notes have been migrated forward into
      // additionalInfo.publicNotes (see addtl below).
      stepNotes: {},
    };
    const meas: SaveMeasurementData = {
      unit,
      dimensions,
      hasDifferentSubstrate,
      ...(hasDifferentSubstrate
        ? {
            substrateMeasurements: {
              unit: substrateUnit,
              dimensions: substrateDimensions,
            },
          }
        : {}),
      stepNotes: {},
    };
    const addtl = buildAdditionalInfoPayload(addtlCarryRef.current, notes);
    return { desc, meas, addtl };
  }, [
    creators,
    title,
    titleLocation,
    alternativeTitles,
    titleAsWritten,
    signatures,
    creationDate,
    mediaDescription,
    hasMultiples,
    editions,
    notes,
    unit,
    dimensions,
    hasDifferentSubstrate,
    substrateUnit,
    substrateDimensions,
  ]);

  const flushSave = useCallback(async () => {
    if (!effectiveCatalogId || isLoading || hasError) return;
    const { desc, meas, addtl } = buildPayload();
    const serialized = JSON.stringify({ desc, meas, addtl });
    if (serialized === lastSerializedRef.current) return;

    setStatus('saving');
    try {
      const saves: Promise<unknown>[] = [
        updateDescription(desc),
        updateMeasurements(meas),
      ];
      if (addtl) {
        saves.push(saveAdditionalInfo(effectiveCatalogId, addtl, ownerId));
      }
      await Promise.all(saves);
      // Once a save succeeds, mirror the saved publicNotes into the
      // carry-through so subsequent buildPayload() calls don't write a
      // stale value back over a future PrivateOnly edit.
      if (addtl) addtlCarryRef.current = addtl;
      lastSerializedRef.current = serialized;
      setStatus('saved');
      setLastSaveTime(new Date());
    } catch (err) {
      logger.error('[useArtworkDetailsForm] save failed', err);
      setStatus('error');
    }
  }, [
    buildPayload,
    effectiveCatalogId,
    hasError,
    isLoading,
    ownerId,
    saveAdditionalInfo,
    setLastSaveTime,
    setStatus,
    updateDescription,
    updateMeasurements,
  ]);

  // Debounced auto-save: any state change schedules a save 2s later.
  useEffect(() => {
    if (isLoading || !effectiveCatalogId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      flushSave();
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [
    isLoading,
    effectiveCatalogId,
    flushSave,
    creators,
    title,
    titleLocation,
    titleAsWritten,
    alternativeTitles,
    mediaDescription,
    signatures,
    creationDate,
    unit,
    dimensions,
    hasDifferentSubstrate,
    substrateUnit,
    substrateDimensions,
    hasMultiples,
    editions,
    notes,
  ]);

  return useMemo<ArtworkDetailsForm>(
    () => ({
      isLoading,
      hasError,
      creators,
      setCreators,
      isCommercial,
      ownerDisplayName,
      title,
      setTitle,
      titleLocation,
      setTitleLocation,
      titleAsWritten,
      setTitleAsWritten,
      alternativeTitles,
      setAlternativeTitles,
      mediaDescription,
      setMediaDescription,
      signatures,
      setSignatures,
      creationDate,
      setCreationDate,
      unit,
      setUnit,
      dimensions,
      setDimensions,
      hasDifferentSubstrate,
      setHasDifferentSubstrate,
      substrateUnit,
      setSubstrateUnit,
      substrateDimensions,
      setSubstrateDimensions,
      hasMultiples,
      setHasMultiples,
      editions,
      setEditions,
      notes,
      setNotes,
      flushSave,
    }),
    [
      isLoading,
      hasError,
      creators,
      isCommercial,
      ownerDisplayName,
      title,
      titleLocation,
      titleAsWritten,
      alternativeTitles,
      mediaDescription,
      signatures,
      creationDate,
      unit,
      dimensions,
      hasDifferentSubstrate,
      substrateUnit,
      substrateDimensions,
      hasMultiples,
      editions,
      notes,
      flushSave,
    ],
  );
};
